import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TrimTextField from "../../components/TrimTextField";
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";
import { INDIGO, SLATE } from "../../constants/Colors";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import useToast from "../../hooks/useToast";
import { TOAST } from "../../constants/ToastMessages";
import LoadingSpinner from "../../components/LoadingSpinner";
import VariableChip from "../../components/VariableChip";
import ArrayChipInput from "../../components/ArrayChipInput";
import JsonEditor from "../../components/JsonEditor";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const DATA_TYPE_OPTIONS = [
  { value: "string",  label: "String"  },
  { value: "number",  label: "Number"  },
  { value: "boolean", label: "Boolean" },
  { value: "array",   label: "Array"   },
  { value: "json",    label: "JSON"    },
];

const DATA_TYPE_COLORS = {
  string:  { bg: SLATE[100],       border: SLATE[300],       text: SLATE[600]       },
  number:  { bg: "#eff6ff",        border: "#bfdbfe",        text: "#1d4ed8"        },
  boolean: { bg: "#f0fdf4",        border: "#bbf7d0",        text: "#15803d"        },
  array:   { bg: "#f5f3ff",        border: "#ddd6fe",        text: "#6d28d9"        },
  json:    { bg: "#fff7ed",        border: "#fed7aa",        text: "#c2410c"        },
};

function DataTypeBadge({ type }) {
  const colors = DATA_TYPE_COLORS[type] || DATA_TYPE_COLORS.string;
  return (
    <Box sx={{ px: "6px", py: "1px", bgcolor: colors.bg, border: `1px solid ${colors.border}`, borderRadius: "4px", flexShrink: 0 }}>
      <Typography sx={{ fontSize: "0.625rem", fontWeight: 700, color: colors.text, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {type}
      </Typography>
    </Box>
  );
}

const EMPTY_FORM = { label: "", variable_name: "", description: "", default_value: "", data_type: "string" };

function toSnakeCase(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_]/g, "")
    .replace(/\s+/g, "_")
    .replace(/^[^a-z]+/, "");
}

function displayDefaultValue(field) {
  if (!field.default_value) return "—";
  if (field.data_type === "boolean") return field.default_value === "true" ? "True" : "False";
  if (field.data_type === "array") {
    try {
      const arr = JSON.parse(field.default_value);
      if (Array.isArray(arr) && arr.length > 0) return arr.join(", ");
      if (Array.isArray(arr) && arr.length === 0) return "—";
    } catch {}
  }
  if (field.data_type === "json") {
    try {
      const obj = JSON.parse(field.default_value);
      const keys = Object.keys(obj);
      if (keys.length === 0) return "{ }";
      const preview = keys.slice(0, 2).map((k) => `${k}: …`).join(", ");
      return `{ ${preview}${keys.length > 2 ? `, +${keys.length - 2} more` : ""} }`;
    } catch {}
    return field.default_value;
  }
  return field.default_value;
}

function renderDefaultValueInput(dataType, value, onChange, error, helperText) {
  if (dataType === "boolean") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Switch
          checked={value === "true"}
          onChange={(e) => onChange(e.target.checked ? "true" : "false")}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: INDIGO[600] },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: INDIGO[400] },
          }}
        />
        <Typography sx={{ fontSize: "0.875rem", color: SLATE[700] }}>
          {value === "true" ? "True" : "False"}
        </Typography>
      </Box>
    );
  }

  if (dataType === "array") {
    return (
      <>
        <ArrayChipInput
          value={value}
          onChange={onChange}
          placeholder="Type and press Enter to add items…"
        />
        {error && (
          <Typography sx={{ fontSize: "0.75rem", color: "#d32f2f", mt: "3px", ml: "14px" }}>
            {helperText}
          </Typography>
        )}
      </>
    );
  }

  if (dataType === "json") {
    return (
      <JsonEditor
        value={value}
        onChange={onChange}
        error={!!error}
        helperText={error ? helperText : ""}
      />
    );
  }

  if (dataType === "number") {
    return (
      <TextField
        fullWidth size="small" type="number"
        placeholder="e.g. 100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={!!error}
        helperText={error ? helperText : ""}
        sx={INPUT_SX}
      />
    );
  }

  // string (default)
  return (
    <TrimTextField
      fullWidth size="small" multiline minRows={3}
      placeholder="Optional — agents will start with this value pre-filled…"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={INPUT_SX}
    />
  );
}

export default function PromptVariables() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();
  const { success } = useToast();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deletingField, setDeletingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setPageMeta({
      description: "Manage instruction fields available to all agents.",
      actions: (
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
          onClick={openAddDialog}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        >
          Add Field
        </Button>
      ),
    });
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFields();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFields = async () => {
    setLoading(true);
    try {
      const res = await makeRequest("/v2/prompt_variable_templates", { method: "GET", authTokens: tokens });
      const sorted = (res?.templates || []).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      setFields(sorted);
    } catch {
      // error toasted
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingField(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const hasChanges = editingField
    ? form.label !== (editingField.label || "")
      || form.description !== (editingField.description || "")
      || form.default_value !== (editingField.default_value || "")
    : form.label.trim().length > 0;

  const openEditDialog = (field) => {
    setEditingField(field);
    setForm({
      label: field.label || "",
      variable_name: field.variable_name || "",
      description: field.description || "",
      default_value: field.default_value || "",
      data_type: field.data_type || "string",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingField(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const handleLabelChange = (val) => {
    setForm((p) => ({
      ...p,
      label: val,
      ...(!editingField ? { variable_name: toSnakeCase(val) } : {}),
    }));
    setFormErrors((p) => ({ ...p, label: false }));
  };

  const handleDataTypeChange = (newType) => {
    setForm((p) => ({ ...p, data_type: newType, default_value: "" }));
    setFormErrors((p) => ({ ...p, default_value: false }));
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!form.label.trim()) errors.label = true;
    if (!editingField && !form.variable_name.trim()) errors.variable_name = true;
    if (!editingField && !/^[a-z][a-z0-9_]*$/.test(form.variable_name)) errors.variable_name = true;

    const dt = form.data_type;
    if (dt === "json" && form.default_value.trim()) {
      try { JSON.parse(form.default_value); } catch { errors.default_value = "Invalid JSON"; }
    }
    if (dt === "number" && form.default_value.trim() && isNaN(Number(form.default_value))) {
      errors.default_value = "Must be a valid number";
    }

    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      const payload = editingField
        ? { label: form.label, description: form.description, default_value: form.default_value }
        : { label: form.label, variable_name: form.variable_name, description: form.description, default_value: form.default_value, variable_type: "static", data_type: form.data_type, is_active: true };

      if (editingField) {
        await makeRequest(`/v2/prompt_variable_templates/${editingField.id}`, {
          method: "PUT",
          body: { prompt_variable_template: payload },
          authTokens: tokens,
        });
      } else {
        await makeRequest("/v2/prompt_variable_templates", {
          method: "POST",
          body: { prompt_variable_template: payload },
          authTokens: tokens,
        });
      }
      success(editingField ? TOAST.agentVariable.saved : TOAST.agentVariable.created);
      handleDialogClose();
      fetchFields();
    } catch {
      // error toasted
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await makeRequest(`/v2/prompt_variable_templates/${deletingField.id}`, {
        method: "DELETE",
        authTokens: tokens,
      });
      setFields((prev) => prev.filter((f) => f.id !== deletingField.id));
      setDeletingField(null);
      success(TOAST.agentVariable.deleted);
    } catch {
      // error toasted
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading agent variables…" />;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Card */}
      <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", overflow: "hidden" }}>

        {/* Card header */}
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${SLATE[200]}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
            Agent Variables
          </Typography>
          {fields.length > 0 && (
            <Box sx={{ px: "10px", py: "2px", bgcolor: SLATE[100], borderRadius: "20px" }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[600] }}>{fields.length}</Typography>
            </Box>
          )}
        </Box>

        {fields.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>📋</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>No variables yet</Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>Add variables to define what agents can be instructed with.</Typography>
          </Box>
        ) : (
          <Box>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.4fr 2fr 1.4fr 72px",
                px: 3,
                py: "10px",
                borderBottom: `1px solid ${SLATE[200]}`,
                bgcolor: SLATE[50],
              }}
            >
              {["Variable", "Description", "Default Value", ""].map((col) => (
                <Typography key={col} sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {col}
                </Typography>
              ))}
            </Box>

            {fields.map((field, idx) => (
              <Box
                key={field.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 2fr 1.4fr 72px",
                  px: 3,
                  py: 2,
                  alignItems: "center",
                  borderBottom: idx < fields.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                  transition: "background 0.15s",
                  "&:hover": { bgcolor: SLATE[50] },
                }}
              >
                {/* Label + chip + data type badge */}
                <Box sx={{ pr: 3 }}>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: SLATE[900], mb: "5px" }}>
                    {field.label || field.variable_name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                    <VariableChip name={field.variable_name} />
                    <DataTypeBadge type={field.data_type || "string"} />
                  </Box>
                </Box>

                {/* Description */}
                <Typography sx={{
                  fontSize: "0.875rem", color: SLATE[500], pr: 3, lineHeight: 1.55,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {field.description || "—"}
                </Typography>

                {/* Default Value */}
                <Typography sx={{
                  fontSize: "0.875rem", pr: 2,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  color: field.default_value ? SLATE[700] : SLATE[400],
                  fontStyle: field.default_value ? "normal" : "italic",
                }}>
                  {displayDefaultValue(field)}
                </Typography>

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                  <Tooltip title="Edit" placement="top">
                    <IconButton size="small" onClick={() => openEditDialog(field)} sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] } }}>
                      <EditIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete" placement="top">
                    <IconButton size="small" onClick={() => setDeletingField(field)} sx={{ color: SLATE[400], "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}>
                      <DeleteIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingField} onClose={() => setDeletingField(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Delete Field</Typography>
          <IconButton size="small" onClick={() => setDeletingField(null)} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 3 }}>
            Are you sure you want to delete <strong>{deletingField?.label || deletingField?.variable_name}</strong>? This will remove it from all agents.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeletingField(null)} variant="outlined" size="small" sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}>
              Cancel
            </Button>
            <Button onClick={handleDelete} disabled={deleting} variant="contained" size="small" sx={{ bgcolor: "#ef4444", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#dc2626", boxShadow: "none" } }}>
              {deleting && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Field Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
            {editingField ? "Edit Field" : "Add Field"}
          </Typography>
          <IconButton size="small" onClick={handleDialogClose} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Label */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Label</Typography>
            <TrimTextField
              fullWidth size="small" placeholder="e.g. Agent Role Definition"
              value={form.label}
              onChange={(e) => handleLabelChange(e.target.value)}
              error={formErrors.label}
              helperText={formErrors.label ? "Label is required" : ""}
              sx={INPUT_SX}
            />
          </Box>

          {/* Variable Name (create only) */}
          {!editingField && (
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Variable Name</Typography>
              <TextField
                fullWidth size="small" placeholder="e.g. agent_role_definition"
                value={form.variable_name}
                onChange={(e) => {
                  setForm((p) => ({ ...p, variable_name: e.target.value.toLowerCase() }));
                  setFormErrors((p) => ({ ...p, variable_name: false }));
                }}
                onBlur={(e) => setForm((p) => ({ ...p, variable_name: e.target.value.trim().toLowerCase() }))}
                error={formErrors.variable_name}
                helperText={formErrors.variable_name ? "Must start with a letter, lowercase letters/numbers/underscores only" : "Auto-derived from label — used as {{variable_name}} in prompts"}
                sx={INPUT_SX}
              />
            </Box>
          )}

          {/* Data Type */}
          {!editingField ? (
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Data Type</Typography>
              <Select
                fullWidth size="small"
                value={form.data_type}
                onChange={(e) => handleDataTypeChange(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  bgcolor: "#ffffff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: SLATE[300] },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: SLATE[400] },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: INDIGO[500] },
                }}
              >
                {DATA_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.875rem" }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ) : (
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>Data Type</Typography>
              <Box sx={{ display: "flex", alignItems: "center", height: "37px" }}>
                <DataTypeBadge type={form.data_type || "string"} />
              </Box>
            </Box>
          )}

          {/* Description */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Description</Typography>
            <TrimTextField
              fullWidth size="small" multiline minRows={2}
              placeholder="Describe what this field is for…"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              sx={INPUT_SX}
            />
          </Box>

          {/* Default Value */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Default Value</Typography>
            {renderDefaultValueInput(
              form.data_type,
              form.default_value,
              (val) => {
                setForm((p) => ({ ...p, default_value: val }));
                setFormErrors((p) => ({ ...p, default_value: false }));
              },
              !!formErrors.default_value,
              typeof formErrors.default_value === "string" ? formErrors.default_value : "",
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button onClick={handleDialogClose} variant="outlined" size="small" sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving || !hasChanges} variant="contained" size="small" sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}>
              {saving && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
              {editingField ? "Save Changes" : "Add Field"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
